/**
 * Wave 6 — Award cockpit: reconciliation states, CR context, CA handoff.
 */
import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Modal,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_EPC_AWARD_ACCEPTANCE } from '@/constants/approval-entity-types';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { formatMoney, formatPercent } from '@/utils/format';
import {
  buildContractReviewContext,
  buildReconciliationView,
  MATERIAL_MISMATCH_PCT,
} from './awardReconciliationUx';
import { pursuitResolvePath } from './resolvePursuitOpportunityId';
import { apiErrorMessage, isDuplicateContractError } from './epcContractErrors';

const { Text, Title } = Typography;

export type EpcAwardCockpitProps = {
  award: Record<string, unknown>;
  onUpdated?: () => void;
};

export const EpcAwardCockpit: React.FC<EpcAwardCockpitProps> = ({ award, onUpdated }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const id = String(award.id || '');
  const status = String(award.status || 'Draft');
  const currency = String(award.currency || 'AED');
  const cr = useMemo(() => buildContractReviewContext(award), [award]);

  const reconQ = useQuery({
    queryKey: ['epc-reconciliations', id],
    queryFn: () => epcCommercialService.listReconciliations(id),
    enabled: Boolean(id),
    staleTime: 10_000,
  });

  const contractsQ = useQuery({
    queryKey: ['epc-contracts'],
    queryFn: () => epcCommercialService.listContracts(),
    staleTime: 30_000,
  });

  const reconRow = ((reconQ.data || []) as Record<string, unknown>[])[0] || null;
  const recon = buildReconciliationView(reconRow);

  const linkedContract = useMemo(() => {
    const list = (contractsQ.data || []) as Record<string, unknown>[];
    return list.find((c) => String(c.award_id || c.awardId || '') === id) || null;
  }, [contractsQ.data, id]);

  const reconcileM = useMutation({
    mutationFn: () => epcCommercialService.reconcileAward(id),
    onSuccess: () => {
      message.success('Reconciliation computed');
      qc.invalidateQueries({ queryKey: ['epc-reconciliations', id] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Reconcile failed'),
  });

  const submitM = useMutation({
    mutationFn: () => epcCommercialService.submitAward(id),
    onSuccess: () => {
      message.success('Award submitted for acceptance');
      qc.invalidateQueries({ queryKey: ['epc-awards'] });
      qc.invalidateQueries({
        queryKey: ['approval-summary', ENTITY_TYPE_EPC_AWARD_ACCEPTANCE, id],
      });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Submit failed'),
  });

  const createContractM = useMutation({
    mutationFn: (allowDuplicate: boolean = false) =>
      epcCommercialService.createContractFromAward({
        awardId: id,
        contractReviewId: cr.contractReviewId || undefined,
        allowDuplicate,
      }),
    onSuccess: (row) => {
      message.success('EPC Contract created');
      qc.invalidateQueries({ queryKey: ['epc-contracts'] });
      onUpdated?.();
      if (row?.id) navigate(`/epc/commercial/contracts/${row.id}`);
    },
    onError: (e: unknown) => {
      if (isDuplicateContractError(e)) {
        Modal.confirm({
          title: 'Contract already registered directly?',
          content: apiErrorMessage(e, 'A directly registered contract exists for this opportunity.'),
          okText: 'Create from award anyway',
          onOk: () => createContractM.mutate(true),
        });
        return;
      }
      message.error(apiErrorMessage(e, 'Contract conversion failed'));
    },
  });

  const basisQ = useQuery({
    queryKey: ['epc-award-estimate-basis', id],
    queryFn: () => epcCommercialService.getAwardEstimateBasis(id),
    enabled: Boolean(id),
  });

  const pinBasisM = useMutation({
    mutationFn: () => {
      const revId =
        String(award.estimate_revision_id || '') ||
        window.prompt('Approved estimate_revision_id (must match CERTIFIED submission pin)') ||
        '';
      if (!revId) throw new Error('estimateRevisionId required');
      return epcCommercialService.setAwardEstimateBasis(id, { estimateRevisionId: revId });
    },
    onSuccess: () => {
      message.success('Award Basis pinned');
      qc.invalidateQueries({ queryKey: ['epc-award-estimate-basis', id] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Pin failed'),
  });

  const costImpactM = useMutation({
    mutationFn: (decision: string) =>
      epcCommercialService.setAwardCostImpact(id, {
        decision,
        reason: decision === 'NO_COST_IMPACT' ? 'Price-only award change' : 'Scope/terms require R(n+1)',
        classification: decision === 'NO_COST_IMPACT' ? 'PRICE_ADJUSTMENT' : 'SCOPE_ADJUSTMENT',
      }),
    onSuccess: () => {
      message.success('Cost impact recorded');
      qc.invalidateQueries({ queryKey: ['epc-reconciliations', id] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Cost impact failed'),
  });

  const basis = (basisQ.data || {}) as Record<string, unknown>;
  const basisStatus = String(basis.status || 'NOT_RECORDED');

  const canSubmit = ['Draft', 'Rejected'].includes(status);
  const canConvert =
    ['Accepted', 'Approved'].includes(status) &&
    recon.allowsContract &&
    !linkedContract;
  const convertBlockedReason = !['Accepted', 'Approved'].includes(status)
    ? 'Award must be Accepted via Approval Runtime first'
    : linkedContract
      ? 'Contract already exists for this award'
      : !award.proposal_id && !award.proposalId && recon.result === 'PENDING'
        ? 'No proposal linked — click Run reconcile to attach the tender proposal and compute variance'
        : !recon.allowsContract
          ? recon.result === 'PENDING'
            ? 'Run Reconcile first — contract conversion requires MATCHED or MINOR_VARIANCE'
            : `Reconciliation ${recon.result} blocks conversion`
          : undefined;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div>
        <Space wrap style={{ marginBottom: 8 }}>
          <Title level={5} style={{ margin: 0 }}>
            {String(award.award_number || 'Award')}
          </Title>
          <Tag>{status}</Tag>
          <Tag>{String(award.award_type || '—')}</Tag>
          <Tag color={recon.badgeColor}>{recon.result}</Tag>
          <Tag color={basisStatus === 'PINNED' || basisStatus === 'LOCKED' ? 'green' : 'orange'}>
            Basis {basisStatus}
          </Tag>
        </Space>
        <Text type="secondary">
          {award.award_value != null
            ? formatMoney(Number(award.award_value), { currency })
            : 'No award value'}
          {String(award.award_type || '').toUpperCase() === 'LNTP' && award.spending_cap != null
            ? ` · Cap ${formatMoney(Number(award.spending_cap), { currency })}`
            : ''}
        </Text>
      </div>

      {['Accepted', 'Approved'].includes(status) && !linkedContract && !canConvert ? (
        <Alert
          type="warning"
          showIcon
          message="Contract conversion blocked"
          description={convertBlockedReason}
        />
      ) : null}

      <Card size="small" title="ESTIMATE BASIS (cost ≠ award value)">
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="Status">{basisStatus}</Descriptions.Item>
          <Descriptions.Item label="Revision">
            {String(basis.estimate_revision_id || '—')}
          </Descriptions.Item>
          <Descriptions.Item label="Estimate cost (cost side)">
            {basis.basis_total_estimated_cost != null
              ? formatMoney(Number(basis.basis_total_estimated_cost), { currency })
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Submitted bid">
            {basis.basis_final_bid_price != null
              ? formatMoney(Number(basis.basis_final_bid_price), { currency })
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Award value (revenue)">
            {basis.award_value != null
              ? formatMoney(Number(basis.award_value), { currency })
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Award delta">
            {basis.award_delta != null
              ? formatMoney(Number(basis.award_delta), { currency })
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Bid margin">
            {basis.bid_margin_pct != null ? `${basis.bid_margin_pct}%` : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Award margin">
            {basis.award_margin_pct != null ? `${basis.award_margin_pct}%` : '—'}
            {basis.margin_erosion_pp != null ? ` · erosion ${basis.margin_erosion_pp}pp` : ''}
          </Descriptions.Item>
        </Descriptions>
        {basisStatus === 'LOCKED' && (
          <Alert
            style={{ marginTop: 8 }}
            type="info"
            showIcon
            message="Award Basis locked by Original Budget"
          />
        )}
        <Space wrap style={{ marginTop: 12 }}>
          <Button
            type="primary"
            disabled={basisStatus === 'LOCKED'}
            loading={pinBasisM.isPending}
            onClick={() => pinBasisM.mutate()}
          >
            Pin Award Basis
          </Button>
          <Button
            disabled={basisStatus === 'LOCKED'}
            loading={costImpactM.isPending}
            onClick={() => costImpactM.mutate('NO_COST_IMPACT')}
          >
            No cost impact
          </Button>
          <Button
            danger
            disabled={basisStatus === 'LOCKED'}
            loading={costImpactM.isPending}
            onClick={() => costImpactM.mutate('REQUIRES_ESTIMATE_REVISION')}
          >
            Requires estimate revision
          </Button>
        </Space>
      </Card>

      <Card size="small" title="Reconciliation">
        <Alert
          type={
            recon.result === 'MATERIAL_MISMATCH'
              ? 'error'
              : recon.result === 'PENDING'
                ? 'info'
                : recon.result === 'MINOR_VARIANCE'
                  ? 'warning'
                  : 'success'
          }
          showIcon
          message={recon.summary}
          description={`Threshold ${MATERIAL_MISMATCH_PCT}% (MATERIAL_MISMATCH_PCT) — computed by domain API, not FE-only.`}
          style={{ marginBottom: 12 }}
        />
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Proposal value">
            {recon.proposalValue != null
              ? formatMoney(recon.proposalValue, { currency })
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Award value">
            {recon.awardValue != null ? formatMoney(recon.awardValue, { currency }) : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Variance">
            {recon.varianceAmount != null
              ? formatMoney(recon.varianceAmount, { currency })
              : '—'}
            {recon.variancePct != null ? ` (${formatPercent(recon.variancePct)})` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Contract conversion">
            {recon.allowsContract ? (
              <Tag color="success">Allowed</Tag>
            ) : (
              <Tag color="error">Blocked</Tag>
            )}
          </Descriptions.Item>
        </Descriptions>
        <Button
          style={{ marginTop: 12 }}
          loading={reconcileM.isPending}
          onClick={() => reconcileM.mutate()}
        >
          Run reconcile
        </Button>
      </Card>

      <Card size="small" title="Contract Review (governance)">
        {cr.linked && cr.path ? (
          <Space direction="vertical">
            <Text>{cr.note}</Text>
            <Button type="link" style={{ padding: 0 }} onClick={() => navigate(cr.path!)}>
              Open Contract Review {cr.contractReviewId!.slice(0, 8)}…
            </Button>
          </Space>
        ) : (
          <Alert type="info" showIcon message={cr.note} />
        )}
      </Card>

      <Card size="small" title="Acceptance & conversion">
        <Space wrap>
          <Button
            type="primary"
            disabled={!canSubmit}
            loading={submitM.isPending}
            onClick={() => submitM.mutate()}
          >
            Submit for acceptance
          </Button>
          <Button
            disabled={!canConvert}
            loading={createContractM.isPending}
            title={convertBlockedReason}
            onClick={() => createContractM.mutate(false)}
          >
            Create EPC Contract
          </Button>
          {linkedContract ? (
            <Button
              type="primary"
              onClick={() => navigate(`/epc/commercial/contracts/${linkedContract.id}`)}
            >
              Open Contract Admin (CA)
            </Button>
          ) : null}
          <Button onClick={() => navigate(pursuitResolvePath('epc_award', id))}>
            Open pursuit
          </Button>
        </Space>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Acceptance uses Approval Runtime (`epc_award_acceptance`). FE never sets Accepted alone.
          CA workspace is Program 12.2 — link out, do not duplicate.
        </Text>
      </Card>

      <Card size="small" title="Approval">
        {id ? (
          <ApprovalPanel
            entityType={ENTITY_TYPE_EPC_AWARD_ACCEPTANCE}
            entityId={id}
            extraInvalidateQueryKeys={[['epc-awards'], ['epc-award', id]]}
          />
        ) : null}
      </Card>
    </Space>
  );
};

export default EpcAwardCockpit;
